package com.ijoin.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import kotlinx.browser.document
import org.w3c.dom.HTMLVideoElement

@Composable
actual fun VideoPlayer(
    videoUrl: String,
    modifier: Modifier
) {
    val videoId = remember { "video-player-${kotlin.random.Random.nextInt(1000000)}" }
    
    Box(modifier = modifier) {
        DisposableEffect(videoUrl) {
            console.log("VideoPlayer: Creating video element for URL:", videoUrl)
            
            // Create video element
            val video = document.createElement("video") as HTMLVideoElement
            video.id = videoId
            video.controls = true
            video.style.width = "100%"
            video.style.height = "100%"
            video.style.objectFit = "cover"
            video.setAttribute("playsinline", "true")
            video.setAttribute("preload", "auto")
            
            // Add comprehensive error handling
            video.addEventListener("error", { event ->
                console.error("VideoPlayer: Error loading video:", videoUrl, event)
                val error = video.error
                if (error != null) {
                    val errorMsg = when (error.code.toInt()) {
                        1 -> "MEDIA_ERR_ABORTED - Загрузка прервана"
                        2 -> "MEDIA_ERR_NETWORK - Ошибка сети"
                        3 -> "MEDIA_ERR_DECODE - Ошибка декодирования"
                        4 -> "MEDIA_ERR_SRC_NOT_SUPPORTED - Формат не поддерживается"
                        else -> "Неизвестная ошибка (код: ${error.code})"
                    }
                    console.error("VideoPlayer: Error details - code:", error.code, "description:", errorMsg)
                } else {
                    console.error("VideoPlayer: Error object is null")
                }
            })
            
            video.addEventListener("abort", {
                console.warn("VideoPlayer: Video load aborted:", videoUrl)
            })
            
            video.addEventListener("stalled", {
                console.warn("VideoPlayer: Video stalled:", videoUrl)
            })
            
            video.addEventListener("suspend", {
                console.warn("VideoPlayer: Video load suspended:", videoUrl)
            })
            
            video.addEventListener("loadstart", {
                console.log("VideoPlayer: Load started:", videoUrl)
            })
            
            video.addEventListener("loadedmetadata", {
                console.log("VideoPlayer: Metadata loaded:", videoUrl, "duration:", video.duration)
            })
            
            video.addEventListener("loadeddata", {
                console.log("VideoPlayer: Data loaded successfully:", videoUrl)
            })
            
            video.addEventListener("canplay", {
                console.log("VideoPlayer: Can play:", videoUrl)
            })
            
            video.addEventListener("canplaythrough", {
                console.log("VideoPlayer: Can play through:", videoUrl)
            })
            
            // For JS target, we need to append to a container
            // Since we can't easily get the Box's DOM node, we'll use a workaround:
            // Find the video element by ID after it's created and append to the nearest parent
            // For now, append to body as a fallback - the Box modifier should handle positioning
            val container = document.createElement("div") as org.w3c.dom.HTMLElement
            container.style.width = "100%"
            container.style.height = "100%"
            container.style.position = "relative"
            container.appendChild(video)
            
            // Append container to body - positioning will be handled by the Box's modifier
            document.body?.appendChild(container)
            
            // Set source after setting up all handlers
            console.log("VideoPlayer: Setting video source:", videoUrl)
            video.src = videoUrl
            
            // Try to load the video
            video.load()
            
            onDispose {
                console.log("VideoPlayer: Releasing video:", videoUrl)
                video.pause()
                video.src = ""
                video.remove()
                container.remove()
            }
        }
    }
}

